/**
 * @description
 * Invoice list component with type filtering for usage vs subscription invoices.
 * Displays invoice history with filtering, search, and status management.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-08
 * @see admin/src/frontend/components/stripe/InvoiceList.test.tsx
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { api } from '../../services/api';

interface Invoice {
  id: string;
  clientId: string;
  stripeInvoiceId: string;
  status: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  periodStart: string | null;
  periodEnd: string | null;
  dueDate: string | null;
  paidAt: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  lineItems: { description: string; amount: number; [key: string]: unknown }[];
  invoiceType: 'usage' | 'subscription';
  thresholdAmount: number | null;
  createdAt: string;
  updatedAt: string;
}

interface InvoiceListProps {
  clientId: string;
  showTypeFilter?: boolean;
}

type InvoiceTypeFilter = 'all' | 'usage' | 'subscription';

/**
 * Invoice list component
 */
export function InvoiceList({ clientId, showTypeFilter = true }: InvoiceListProps) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<InvoiceTypeFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const invoices = await api.getInvoices(clientId, typeFilter === 'all' ? undefined : typeFilter);
      setInvoices(invoices);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [clientId, typeFilter]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const formatCurrency = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'open':
        return 'bg-yellow-100 text-yellow-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'void':
        return 'bg-red-100 text-red-800';
      case 'uncollectible':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: 'usage' | 'subscription') => {
    return type === 'usage' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800';
  };

  const getTypeIcon = (type: 'usage' | 'subscription') => {
    return type === 'usage' ? (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ) : (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  };

  // Filter invoices by search term
  const filteredInvoices = invoices.filter(invoice => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      invoice.stripeInvoiceId.toLowerCase().includes(searchLower) ||
      invoice.status.toLowerCase().includes(searchLower) ||
      invoice.lineItems.some(item => 
        item.description.toLowerCase().includes(searchLower)
      )
    );
  });

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Invoices</h3>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Type Filter Tabs */}
      {showTypeFilter && (
        <div className="flex gap-1 mb-4 p-1 bg-muted rounded-md">
          <button
            onClick={() => setTypeFilter('all')}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              typeFilter === 'all'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            All ({invoices.length})
          </button>
          <button
            onClick={() => setTypeFilter('usage')}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              typeFilter === 'usage'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Usage ({invoices.filter(inv => inv.invoiceType === 'usage').length})
          </button>
          <button
            onClick={() => setTypeFilter('subscription')}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              typeFilter === 'subscription'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Subscription ({invoices.filter(inv => inv.invoiceType === 'subscription').length})
          </button>
        </div>
      )}

      {/* Invoices List */}
      {filteredInvoices.length > 0 ? (
        <div className="space-y-3">
          {filteredInvoices.map((invoice) => (
            <div key={invoice.id} className="border rounded-md p-4 hover:bg-muted/50 transition-colors">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(invoice.invoiceType)}
                    <span className="font-mono text-sm">{invoice.stripeInvoiceId}</span>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(invoice.invoiceType)}`}>
                    {invoice.invoiceType}
                  </span>
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(invoice.status)}`}>
                    {invoice.status}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold">
                    {formatCurrency(invoice.amountDue, invoice.currency)}
                  </span>
                  {invoice.hostedInvoiceUrl && (
                    <a
                      href={invoice.hostedInvoiceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-md border border-input bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-accent"
                    >
                      View Invoice
                    </a>
                  )}
                </div>
              </div>

              {/* Invoice Details */}
              <div className="grid gap-2 text-sm text-muted-foreground">
                <div className="flex justify-between">
                  <span>Due Date:</span>
                  <span>{formatDate(invoice.dueDate)}</span>
                </div>
                {invoice.periodStart && invoice.periodEnd && (
                  <div className="flex justify-between">
                    <span>Period:</span>
                    <span>{formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}</span>
                  </div>
                )}
                {invoice.paidAt && (
                  <div className="flex justify-between">
                    <span>Paid:</span>
                    <span>{formatDate(invoice.paidAt)}</span>
                  </div>
                )}
                {invoice.invoiceType === 'usage' && invoice.thresholdAmount && (
                  <div className="flex justify-between">
                    <span>Threshold:</span>
                    <span>{formatCurrency(invoice.thresholdAmount, invoice.currency)}</span>
                  </div>
                )}
              </div>

              {/* Line Items */}
              {invoice.lineItems.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <h4 className="text-sm font-medium mb-2">Line Items</h4>
                  <div className="space-y-1">
                    {invoice.lineItems.map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span>{item.description}</span>
                        <span>{formatCurrency(item.amount, invoice.currency)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <svg className="h-12 w-12 text-muted-foreground mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h4 className="text-lg font-medium mb-2">No Invoices</h4>
          <p className="text-sm text-muted-foreground">
            {typeFilter === 'all' 
              ? "This client doesn't have any invoices yet."
              : `No ${typeFilter} invoices found.`
            }
          </p>
        </div>
      )}
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
